import { useState } from 'react'
import {
  Typography, Box, Stack,
} from '@mui/material'
import {
  Phone as CallIcon,
  Groups as MeetingIcon,
  PresentToAll as DemoIcon,
  LocationOn as SiteIcon,
  MoreHoriz as OtherIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { leadFollowUpsApi, FollowUpType, CreateFollowUpPayload } from '../../lib/api/leadFollowUpsApi'
import { toast } from 'react-toastify'
import { EntityModal } from '../shared/EntityModal'
import { EntityFormSection, EntityDrawerInput, EntityDrawerSelect } from '../shared/EntityFormDrawer'

const TYPE_OPTIONS: { value: FollowUpType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'CALL',       label: 'Call',       icon: <CallIcon    sx={{ fontSize: 16 }} />, color: '#0070d2' },
  { value: 'MEETING',    label: 'Meeting',    icon: <MeetingIcon sx={{ fontSize: 16 }} />, color: '#7b1fa2' },
  { value: 'DEMO',       label: 'Demo',       icon: <DemoIcon    sx={{ fontSize: 16 }} />, color: '#00838f' },
  { value: 'SITE_VISIT', label: 'Site Visit', icon: <SiteIcon    sx={{ fontSize: 16 }} />, color: '#2e7d32' },
  { value: 'OTHER',      label: 'Other',      icon: <OtherIcon   sx={{ fontSize: 16 }} />, color: '#f57c00' },
]

const REMINDER_OPTIONS = [
  { value: '',     label: 'No reminder'    },
  { value: '5',    label: '5 min before'  },
  { value: '10',   label: '10 min before' },
  { value: '15',   label: '15 min before' },
  { value: '30',   label: '30 min before' },
  { value: '60',   label: '1 hour before' },
  { value: '1440', label: '1 day before'  },
]

interface Props {
  open:        boolean
  leadId:      string
  leadName:    string
  onClose:     () => void
  onSaved?:    () => void
  defaultType?: FollowUpType
}

const getLocalDateTimeString = () => {
  const now = new Date()
  const tzOffset = now.getTimezoneOffset() * 60000
  return new Date(now.getTime() - tzOffset).toISOString().slice(0, 16)
}

export function FollowUpModal({ open, leadId, leadName, onClose, onSaved, defaultType = 'CALL' }: Props) {
  const qc = useQueryClient()
  const [type,        setType]        = useState<FollowUpType>(defaultType)
  const [scheduledAt, setScheduledAt] = useState(getLocalDateTimeString())
  const [notes,       setNotes]       = useState('')
  const [reminder,    setReminder]    = useState('')

  const reset = () => { setType(defaultType); setScheduledAt(getLocalDateTimeString()); setNotes(''); setReminder('') }

  const mut = useMutation({
    mutationFn: (payload: CreateFollowUpPayload) => leadFollowUpsApi.create(leadId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead-follow-ups', leadId] })
      qc.invalidateQueries({ queryKey: ['lead-activities',  leadId] })
      toast.success('Event scheduled')
      reset(); onClose()
      onSaved?.()
    },
    onError: () => toast.error('Failed to schedule event'),
  })

  const handleSave = () => {
    if (!scheduledAt) { toast.error('Please select a date and time'); return }
    mut.mutate({
      type,
      scheduledAt:     new Date(scheduledAt).toISOString(),
      notes:           notes.trim() || undefined,
      reminderMinutes: reminder ? Number(reminder) : undefined,
    })
  }

  const selectedType = TYPE_OPTIONS.find(t => t.value === type)!

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title="Schedule Event"
      submitLabel="Schedule"
      isSubmitting={mut.isPending}
      submitDisabled={!scheduledAt || new Date(scheduledAt) < new Date(getLocalDateTimeString())}
      maxWidth="xs"
      icon={<CalendarIcon sx={{ fontSize: 18 }} />}
      headerExtra={
        <Typography fontSize="12px" color="rgba(255,255,255,0.7)" mt={0.5}>
          For: <strong>{leadName}</strong>
        </Typography>
      }
      submitProps={{
        sx: {
          bgcolor: selectedType.color,
          '&:hover': { bgcolor: selectedType.color, opacity: 0.9 }
        }
      }}
    >
      <EntityFormSection columns={1}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {TYPE_OPTIONS.map(opt => (
            <Box key={opt.value} onClick={() => setType(opt.value)} sx={{
              flex: '1 1 56px', py: 1, px: 0.5, borderRadius: '8px',
              cursor: 'pointer', textAlign: 'center',
              border: `2px solid ${type === opt.value ? opt.color : '#e5e7eb'}`,
              bgcolor: type === opt.value ? `${opt.color}15` : '#fafafa',
              transition: 'all 0.15s',
            }}>
              <Box sx={{ color: opt.color, display: 'flex', justifyContent: 'center', mb: 0.25 }}>{opt.icon}</Box>
              <Typography fontSize="10px" fontWeight={700} color={type === opt.value ? opt.color : 'text.secondary'}>
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <EntityDrawerInput
          type="datetime-local"
          label="Date & Time"
          value={scheduledAt}
          onChange={v => setScheduledAt(v)}
          required
          inputProps={{ min: getLocalDateTimeString() }}
        />

        <EntityDrawerSelect
          label="Reminder"
          value={reminder}
          onChange={v => setReminder(v)}
          options={REMINDER_OPTIONS}
        />

        <EntityDrawerInput
          multiline
          rows={3}
          label="Notes (optional)"
          placeholder="What to discuss, agenda, etc."
          value={notes}
          onChange={v => setNotes(v)}
        />
      </EntityFormSection>
    </EntityModal>
  )
}
